if (!customElements.get('editorial-category-feed')) {
  const editorialCategoryFeedCache = new Map();

  class EditorialCategoryFeed extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.grid = this.querySelector('[data-category-feed-grid]');
      this.status = this.querySelector('[data-category-feed-status]');
      this.feedUrl = this.dataset.feedUrl;
      this.articlesToShow = Math.min(Math.max(Number(this.dataset.articlesToShow) || 4, 2), 4);

      if (!this.grid || !this.feedUrl) {
        this.showError();
        return;
      }

      if (this.dataset.designMode === 'true' || !('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '1000px 0px' });

      this.observer.observe(this);
    }

    async load() {
      if (this.loadingPromise) return this.loadingPromise;

      this.loadingPromise = this.fetchCards();
      return this.loadingPromise;
    }

    async fetchCards() {
      try {
        const useCache = this.dataset.designMode !== 'true';
        let markupPromise = useCache ? editorialCategoryFeedCache.get(this.feedUrl) : null;

        if (!markupPromise) {
          markupPromise = fetch(this.feedUrl, {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          }).then((response) => {
            if (!response.ok) throw new Error(`Category feed request failed: ${response.status}`);
            return response.text();
          });
          if (useCache) editorialCategoryFeedCache.set(this.feedUrl, markupPromise);
        }

        const markup = await markupPromise;
        const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
        const sourceGrid = documentFragment.querySelector('[data-editorial-tag-grid]');
        const sourceCards = sourceGrid ? Array.from(sourceGrid.querySelectorAll('[data-editorial-card]')) : [];

        if (!sourceCards.length) {
          this.showEmpty();
          return;
        }

        const cards = sourceCards.slice(0, this.articlesToShow).map((sourceCard) => {
          const card = document.importNode(sourceCard, true);
          const title = card.querySelector('.editorial-blog__card-title');

          if (title && title.tagName === 'H2') {
            const replacement = document.createElement('h3');
            replacement.className = title.className;
            replacement.innerHTML = title.innerHTML;
            title.replaceWith(replacement);
          }

          return card;
        });

        this.grid.replaceChildren(...cards);
        this.grid.setAttribute('aria-busy', 'false');
        this.status.hidden = true;
        this.classList.remove('is-loading');
        this.classList.add('is-loaded');
      } catch (error) {
        if (this.dataset.designMode !== 'true') editorialCategoryFeedCache.delete(this.feedUrl);
        this.showError();
      }
    }

    showEmpty() {
      this.grid.setAttribute('aria-busy', 'false');
      this.status.textContent = this.dataset.designMode === 'true'
        ? 'No published articles currently use this tag.'
        : '';
      this.status.hidden = this.dataset.designMode !== 'true';
      this.classList.remove('is-loading');
      this.classList.add('is-empty');

      if (this.dataset.designMode !== 'true') this.hidden = true;
    }

    showError() {
      if (this.grid) this.grid.setAttribute('aria-busy', 'false');
      if (this.status) this.status.textContent = 'Stories could not be loaded. Use View more to browse this category.';
      this.classList.remove('is-loading');
      this.classList.add('has-error');
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  customElements.define('editorial-category-feed', EditorialCategoryFeed);
}
